/**
 * Where the files of an imported folder or archive go in a Flow project, decided before anything is sent so the person
 * sees it and can confirm: every service under `services/<name>/`, the project's own files at its root.
 *
 * - A folder or archive whose root already has `services/` is kept as it is (a project exported from Flow, a monorepo
 *   laid out the same way).
 * - A service is a folder holding one of SERVICE_MARKERS (package.json, a Dockerfile, pyproject.toml or
 *   requirements.txt, go.mod, pom.xml, or a static index.html); the outermost one wins (a `public/index.html` inside a
 *   Node app is part of that app). Each becomes `services/<its folder name>`.
 * - When the root itself is an app (a marker at the root that is not a workspace package.json), or nothing is
 *   recognised at all, the whole thing becomes the one service `services/app`.
 *
 * Paths are relative to what was picked or dropped. One folder wrapping everything (`my-app/…`, what zipping a folder
 * gives) is taken off first; its name is a hint for the project's name.
 *
 * Pure functions: the import dialog and the tests share them.
 */

/** Marker file → the kind of service it says, as shown to the person. */
export const SERVICE_MARKERS = {
  'package.json': 'Node.js',
  Dockerfile: 'Docker',
  'pyproject.toml': 'Python',
  'requirements.txt': 'Python',
  'go.mod': 'Go',
  'pom.xml': 'Java',
  'index.html': 'Static site',
};

/** Folders of generated files an import leaves out (on top of node_modules and .git, as the tree upload does). */
export const BUILD_OUTPUT_FOLDERS = new Set([
  'dist', 'build', '.next', '.nuxt', '.svelte-kit', '.output', '.turbo', '.cache', '.parcel-cache', '.vite', 'coverage',
  '__pycache__', '.venv', 'venv', '.pytest_cache', '.mypy_cache', '.tox', 'target', '.gradle',
]);

/** The project's metadata file: never imported as a file (the new project writes its own); its name/description are read. */
export const PROJECT_METADATA_PATH = '.mia/project.json';

const README_NAMES = ['readme.md', 'readme', 'readme.txt', 'readme.markdown', 'readme.rst'];

const splitPath = (relPath) => String(relPath || '').replace(/\\/g, '/').split('/').filter((segment) => segment && segment !== '.');
const dirOf = (relPath) => splitPath(relPath).slice(0, -1).join('/');
const baseOf = (relPath) => splitPath(relPath).pop() || '';

/** True when the path is inside a build-output folder. */
export function isBuildOutput(relPath) {
  return splitPath(relPath).slice(0, -1).some((segment) => BUILD_OUTPUT_FOLDERS.has(segment));
}

/**
 * The one folder every path is inside (`my-app` for `my-app/a`, `my-app/b/c`), or '' when there is none (files at the
 * top, or more than one top folder).
 */
export function commonRootFolder(relPaths) {
  let root = null;
  for (const relPath of relPaths) {
    const segments = splitPath(relPath);
    if (segments.length < 2) { return ''; }
    if (root === null) { root = segments[0]; } else if (root !== segments[0]) { return ''; }
  }
  return root || '';
}

/** `My App!` → `my-app`; '' when nothing usable is left. At most 40 characters. */
export function slugify(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/^@[^/]+\//, '')
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[-._]+|[-._]+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 40)
    .replace(/[-._]+$/, '');
}

/** A service name from a folder name, unique among `taken` (`api`, `api-2`, …). */
export function serviceName(raw, taken = new Set()) {
  const base = slugify(raw) || 'app';
  let name = base;
  for (let index = 2; taken.has(name); index += 1) { name = `${base}-${index}`; }
  taken.add(name);
  return name;
}

function markerKind(relPath) {
  return SERVICE_MARKERS[baseOf(relPath)] || null;
}

/**
 * @param {string[]} relPaths the files to import (already without node_modules, .git, build output and junk)
 * @param {object} [options]
 * @param {boolean} [options.rootIsWorkspace] the root package.json declares `workspaces` (a monorepo, not one app)
 * @returns {{
 *   mode: 'services'|'multi'|'single'|'empty',
 *   rootFolder: string,
 *   services: Array<{name: string, source: string, kinds: string[], fileCount: number}>,
 *   destOf: (relPath: string) => string|null,
 * }} `source`: the service's folder in what was picked ('' = its root); `destOf`: the project path of a file, null when
 *   it is not imported (the project's own `.mia/project.json`).
 */
export function planLayout(relPaths, { rootIsWorkspace = false } = {}) {
  const rootFolder = commonRootFolder(relPaths);
  const strip = (relPath) => {
    const segments = splitPath(relPath);
    return (rootFolder ? segments.slice(1) : segments).join('/');
  };
  const inner = relPaths.map(strip).filter(Boolean);
  const skipMetadata = (path) => (path === PROJECT_METADATA_PATH ? null : path);

  if (!inner.length) {
    return { mode: 'empty', rootFolder, services: [], destOf: () => null };
  }

  // Already laid out as a Flow project: kept as it is.
  if (inner.some((path) => /^services\/[^/]+\/./.test(path))) {
    const counts = new Map();
    const kinds = new Map();
    for (const path of inner) {
      const match = /^services\/([^/]+)\/(.+)$/.exec(path);
      if (!match) { continue; }
      counts.set(match[1], (counts.get(match[1]) || 0) + 1);
      const kind = !match[2].includes('/') && markerKind(match[2]);
      if (kind) { kinds.set(match[1], [...new Set([...(kinds.get(match[1]) || []), kind])]); }
    }
    const services = [...counts.keys()].sort().map((name) => ({ name, source: `services/${name}`, kinds: kinds.get(name) || [], fileCount: counts.get(name) }));
    return { mode: 'services', rootFolder, services, destOf: (relPath) => skipMetadata(strip(relPath)) || null };
  }

  const markerDirs = new Map(); // folder → kinds
  for (const path of inner) {
    const kind = markerKind(path);
    if (!kind) { continue; }
    const dir = dirOf(path);
    markerDirs.set(dir, [...new Set([...(markerDirs.get(dir) || []), kind])]);
  }

  const rootIsApp = markerDirs.has('') && !rootIsWorkspace;
  const nested = [...markerDirs.keys()].filter((dir) => dir !== '');
  // The outermost marker folders: a folder inside another service is part of it.
  const outermost = nested
    .filter((dir) => !nested.some((other) => other !== dir && dir.startsWith(`${other}/`)))
    .sort();

  if (rootIsApp || !outermost.length) {
    const kinds = markerDirs.get('') || [];
    const services = [{ name: 'app', source: '', kinds, fileCount: inner.filter((path) => path !== PROJECT_METADATA_PATH).length }];
    return {
      mode: 'single',
      rootFolder,
      services,
      destOf: (relPath) => {
        const path = skipMetadata(strip(relPath));
        return path ? `services/app/${path}` : null;
      },
    };
  }

  // Two services in folders of the same name (`frontend/app`, `backend/app`) are named after their parent folders.
  const baseCounts = outermost.reduce((counts, dir) => counts.set(slugify(baseOf(dir)), (counts.get(slugify(baseOf(dir))) || 0) + 1), new Map());
  const nameFor = (dir) => {
    const parent = baseOf(dirOf(dir));
    return baseCounts.get(slugify(baseOf(dir))) > 1 && parent ? parent : baseOf(dir);
  };
  const taken = new Set();
  const services = outermost.map((dir) => ({ name: serviceName(nameFor(dir), taken), source: dir, kinds: markerDirs.get(dir), fileCount: 0 }));
  const serviceOf = (path) => services.find((service) => path.startsWith(`${service.source}/`));
  for (const path of inner) {
    const service = serviceOf(path);
    if (service) { service.fileCount += 1; }
  }
  return {
    mode: 'multi',
    rootFolder,
    services,
    destOf: (relPath) => {
      const path = skipMetadata(strip(relPath));
      if (!path) { return null; }
      const service = serviceOf(path);
      // Files outside every service (a README, docker-compose.yml, docs) stay at the project's root.
      return service ? `services/${service.name}/${path.slice(service.source.length + 1)}` : path;
    },
  };
}

/**
 * Which files tell the project's name and description: its `.mia/project.json` (an export from Flow), the README and
 * the package.json at the root, else those of the first service. Returned as paths of `relPaths`.
 */
export function infoSources(relPaths, layout) {
  const prefix = layout.rootFolder ? `${layout.rootFolder}/` : '';
  const byInner = new Map();
  for (const relPath of relPaths) {
    const clean = splitPath(relPath).join('/');
    if (!prefix || clean.startsWith(prefix)) { byInner.set(clean.slice(prefix.length), relPath); }
  }
  const lowerMap = new Map([...byInner.keys()].map((path) => [path.toLowerCase(), path]));
  const firstSource = layout.services[0]?.source || '';
  const folders = [...new Set(['', firstSource])];
  const find = (names) => {
    for (const folder of folders) {
      for (const name of names) {
        const hit = lowerMap.get((folder ? `${folder}/${name}` : name).toLowerCase());
        if (hit) { return byInner.get(hit); }
      }
    }
    return null;
  };
  return {
    metadata: byInner.get(PROJECT_METADATA_PATH) || null,
    readme: find(README_NAMES),
    packageJson: find(['package.json']),
    rootPackageJson: byInner.get('package.json') || null,
  };
}

/** `my-cool_app` → `My cool app`. */
export function humanize(raw) {
  const text = String(raw || '')
    .replace(/^@[^/]+\//, '')
    .replace(/\.(zip|tar|tgz|tar\.gz)$/i, '')
    .replace(/[-_.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

const parseJson = (text) => {
  try { return JSON.parse(text); } catch { return null; }
};

/** The README's title (`# Title` or a setext title) and its first paragraph of prose, markdown taken off. */
export function readmeSummary(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  let title = '';
  const paragraph = [];
  let inFence = false;
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (line.startsWith('```')) { inFence = !inFence; continue; }
    if (inFence) { continue; }
    if (!title) {
      const atx = /^#\s+(.+?)\s*#*$/.exec(line);
      if (atx) { title = atx[1]; continue; }
      if (line && /^=+$/.test((lines[index + 1] || '').trim())) { title = line; index += 1; continue; }
    }
    if (/^(#|<|!\[|\[!\[|[-*_]{3,}$|\|)/.test(line)) {
      if (paragraph.length) { break; }
      continue;
    }
    if (!line) {
      if (paragraph.length) { break; }
      continue;
    }
    paragraph.push(line);
  }
  const clean = (value) => value
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[`*_]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { title: clean(title), description: clean(paragraph.join(' ')) };
}

export const MAX_NAME_LENGTH = 60;
export const MAX_DESCRIPTION_LENGTH = 280;

const fit = (text, max) => {
  const value = String(text || '').trim();
  if (value.length <= max) { return value; }
  return `${value.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
};

/**
 * The project's name and description, from (first that says something): `.mia/project.json`, the README, the
 * package.json, the wrapping folder's or the archive's name.
 * @param {{metadata?: string, readme?: string, packageJson?: string}} texts the files' contents (missing = unknown)
 */
export function projectInfo(texts = {}, { rootFolder = '', archiveName = '' } = {}) {
  const metadata = parseJson(texts.metadata || '') || {};
  const pkg = parseJson(texts.packageJson || '') || {};
  const readme = readmeSummary(texts.readme || '');
  const name = [metadata.name, readme.title, humanize(pkg.name), humanize(rootFolder), humanize(archiveName)]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find(Boolean) || 'Imported project';
  const description = [metadata.description, pkg.description, readme.description]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find(Boolean) || '';
  return { name: fit(name, MAX_NAME_LENGTH), description: fit(description, MAX_DESCRIPTION_LENGTH) };
}

/** True when a package.json declares workspaces (its folder is a monorepo, not one app). */
export function declaresWorkspaces(packageJsonText) {
  const pkg = parseJson(packageJsonText || '');
  return Boolean(pkg && (Array.isArray(pkg.workspaces) || Array.isArray(pkg.workspaces?.packages)));
}
